// src/pages/Signin.jsx
import { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import supabase from '../lib/supabaseBrowser';
import { AuthContext } from '../context/AuthContext';
import { loginUser } from '../api/auth';
import { useSubmissionLock } from '../hooks/useSubmissionLock';

const API_BASE = process.env.REACT_APP_API_URL;

/**
 * Signin:
 * - Primary: Supabase password sign-in
 * - Fallback: backend /auth/login (Prisma/bcrypt)
 * - Auth storage: JWT in localStorage as accessToken
 */
function Signin() {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const { setUser, refreshMe } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const { run: runSubmission, isPending } = useSubmissionLock();

  const finishLogin = async ({ token, user }) => {
    if (!token) {
      setError('Login succeeded, but no token was returned.');
      return;
    }

    localStorage.setItem('accessToken', token);
    setUser?.(user || null);

    // Hydrate full user from backend, now that token exists
    await refreshMe?.();

    const role = (user?.role || '').toUpperCase();
    if (role === 'TENANT') navigate('/tenant-portal');
    else navigate('/dashboard');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading || isPending('login')) return;

    await runSubmission('login', async () => {
      setError('');
      setLoading(true);

      if (!API_BASE) {
        setError('Missing REACT_APP_API_URL (frontend environment variable).');
        setLoading(false);
        return;
      }

      const email = e.target.email.value.trim();
      const password = e.target.password.value;

      if (!email || !password) {
        setError('Please enter email and password.');
        setLoading(false);
        return;
      }

      try {
      console.log('Attempting Supabase login', { email });

      // -------------------------
      // 1) Try Supabase login
      // -------------------------
      const { data: supaData, error: supaError } =
        await supabase.auth.signInWithPassword({
          email,
          password,
        });

      if (supaError) {
        // Invalid Supabase credentials can fall through to backend fallback
        if (supaError.status !== 400 && supaError.status !== 401) {
          console.error('Supabase login error:', supaError);
          setError('Supabase login failed. Check console for details.');
          setLoading(false);
          return;
        }
      } else if (supaData?.session?.access_token) {
        const accessToken = supaData.session.access_token;

        try {
          const res = await fetch(`${API_BASE}/api/auth/session`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ access_token: accessToken }),
          });

          if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            console.error('/auth/session failed', res.status, body);
            setError(
              body?.error ||
                'Failed to create server session from Supabase login.'
            );
            setLoading(false);
            return;
          }

          const body = await res.json();
          const user = body.user ?? body;

          await finishLogin({ token: body.token || accessToken, user });
          return;
        } catch (err) {
          console.error('Failed to POST /auth/session', err);
          setError('Failed to create server session. Check server logs.');
          setLoading(false);
          return;
        }
      }

      // -------------------------
      // 2) Fallback -> backend login (Prisma/Bcrypt)
      // -------------------------
      console.log('Attempting backend login fallback', { email });

      let data;
      try {
        const resp = await loginUser({ email, password });
        data = resp?.data ?? resp;
      } catch (err) {
        const status = err?.response?.status;
        const body = err?.response?.data;
        console.error('/auth/login failed', status, body || err);
        setError(
          body?.message ||
            body?.error ||
            'Login failed. Check credentials.'
        );
        setLoading(false);
        return;
      }

      const token = data?.token ?? null;
      const user = data?.user ?? data;

      await finishLogin({ token, user });
      } catch (err) {
        console.error('Signin unexpected error', err);
        setError(err?.message || 'Login failed unexpectedly.');
        setLoading(false);
      }
    });
  };

  return (
    <div className="flex h-screen">
      {/* LEFT SIDE - Login Form */}
      <div className="w-full md:w-1/2 flex items-center justify-center bg-gradient-to-br from-[#111] via-[#1a1a1a] to-[#2b2b2b] p-8">
        <div className="bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl p-10 w-full max-w-md shadow-2xl">
          <div className="text-center mb-10">
            <img
              src="https://cdn.prod.website-files.com/638fda8f8e2f041af84cb5a7/6824d581ae24f69d45e26536_beem%20logo%203.png"
              alt="Beem Logo"
              className="w-24 mx-auto mb-4"
            />
            <h2 className="text-3xl font-bold text-white mb-2">Welcome back</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label
                htmlFor="email"
                className="block mb-1 text-sm font-medium text-gray-300"
              >
                Email address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                className="w-full px-4 py-3 rounded-lg bg-white/15 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block mb-1 text-sm font-medium text-gray-300"
              >
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                required
                className="w-full px-4 py-3 rounded-lg bg-white/15 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
                placeholder="••••••••"
              />
            </div>

            <div className="flex items-center justify-between text-sm text-gray-400">
              <label className="flex items-center gap-2">
                <input type="checkbox" className="accent-[#AF9B8A] rounded" />
                Remember me
              </label>
              <Link
                to="/forgot-password"
                className="hover:text-[#AF9B8A] transition-colors"
              >
                Forgot password?
              </Link>
            </div>

            {error && (
              <p className="text-red-400 text-center text-sm mt-2">{error}</p>
            )}

<button
  type="submit"
  disabled={loading}
  className={`w-full mt-4 text-white font-semibold py-3 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 ${
    loading
      ? "bg-[#9e8a78] opacity-80 cursor-not-allowed"
      : "bg-[#AF9B8A] hover:bg-[#9e8a78]"
  }`}
>
  {loading ? (
    <>
      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
      Logging you in...
    </>
  ) : (
    "Log In"
  )}
</button>
          </form>

          <p className="mt-8 text-center text-gray-400 text-sm">
            New to Beem?{' '}
            <Link to="/sign-up" className="text-[#AF9B8A] hover:underline">
              Create an account
            </Link>
          </p>
        </div>
      </div>

      {/* RIGHT SIDE - Illustration */}
      <div className="hidden md:block w-1/2 relative">
        <img
          src="https://cdn.prod.website-files.com/638fda8f8e2f041af84cb5a7/68e10ee19b260e7f43fddd20_ChatGPT%20Image%20Oct%204%2C%202025%2C%2012_26_41%20PM.png"
          alt="City illustration"
          className="object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-black/20 to-transparent" />
      </div>
    </div>
  );
}

export default Signin;
